import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { webSocketService } from '../services/WebSocketService';
import { apiService, BoitierStatus, Alert } from '../services/ApiService';

interface SondeContextType {
  bains: BoitierStatus[];
  alerts: Alert[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

const SondeContext = createContext<SondeContextType | undefined>(undefined);

interface SondeProviderProps {
  children: ReactNode;
}

export const SondeProvider: React.FC<SondeProviderProps> = ({ children }) => {
  const [bains, setBains] = useState<BoitierStatus[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger toutes les données initiales
  const loadAllData = async () => {
    try {
      setLoading(true);
      // Charger les données des bains
      const bainsData = await apiService.getAllBains();
      setBains(bainsData);
      
      // Charger les alertes actives
      const alertsData = await apiService.getAlerts('active');
      setAlerts(alertsData);
      
      setError(null);
    } catch (err: any) {
      // Erreur silencieuse
      setError(`Impossible de charger les données: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au démarrage
  useEffect(() => {
        loadAllData();
  }, []);

  // Initialiser la connexion WebSocket une seule fois au chargement de l'application
  useEffect(() => {
    // Connecter au WebSocket si pas déjà connecté
    webSocketService.connect();
    
    // Ne déconnecter le WebSocket que lorsque l'application est complètement fermée
    return () => {
      // Cette fonction ne s'exécutera qu'à la fermeture complète de l'application
      // Pas lors des navigations internes
    };
  }, []);
  
  // Gérer les événements WebSocket séparément
  useEffect(() => {
    // Fonction pour gérer les mises à jour des bains
    const handleBainUpdate = (data: BoitierStatus) => {
      setBains(prevBains => {
        const index = prevBains.findIndex(bain => bain.boitier_id === data.boitier_id);
        
        if (index !== -1) {
          // Mettre à jour un bain existant
          const updatedBains = [...prevBains];
          updatedBains[index] = data;
          return updatedBains;
        } else {
          // Ajouter un nouveau bain
          return [...prevBains, data];
        }
      });
    };

    // Fonction pour gérer les nouvelles alertes
    const handleNewAlert = (data: Alert) => {
      setAlerts(prevAlerts => {
        // Vérifier si l'alerte existe déjà
        const exists = prevAlerts.some(alert => alert.id === data.id);
        if (!exists) {
          return [...prevAlerts, data];
        }
        return prevAlerts;
      });
    };

    // Fonction pour gérer les alertes résolues
    const handleAlertResolved = (data: { id: number }) => {
      setAlerts(prevAlerts => 
        prevAlerts.map(alert => 
          alert.id === data.id ? { ...alert, etat: 'resolved' } : alert
        )
      );
    };

    // Fonction pour gérer les lectures capteur en temps-réel (push MQTT → WS)
    const handleReading = (data: any) => {
      if (!data || !data.boitier_id) return;
      setBains(prevBains => {
        const index = prevBains.findIndex(bain => bain.boitier_id === data.boitier_id);
        if (index !== -1) {
          const updatedBains = [...prevBains];
          updatedBains[index] = {
            ...updatedBains[index],
            last_reading: data.data || data,
            sensors_active: true,
          };
          return updatedBains;
        }
        return prevBains;
      });
    };

    // S'abonner aux événements
    webSocketService.on('bain_update', handleBainUpdate);
    webSocketService.on('reading', handleReading);
    webSocketService.on('new_alert', handleNewAlert);
    webSocketService.on('alert_resolved', handleAlertResolved);

    // Nettoyer les abonnements lors du démontage, mais ne pas fermer la connexion
    return () => {
      webSocketService.off('bain_update', handleBainUpdate);
      webSocketService.off('reading', handleReading);
      webSocketService.off('new_alert', handleNewAlert);
      webSocketService.off('alert_resolved', handleAlertResolved);
      // Ne pas déconnecter ici - webSocketService.disconnect();
    };
  }, []);

  // Polling de fallback : recharger les données toutes les 30s si le WS n'est pas connecté
  // + activer le polling immédiatement si le WS signale une perte de connexion
  const fallbackIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Polling de fallback toutes les 30 secondes
    fallbackIntervalRef.current = setInterval(() => {
      if (!webSocketService.isConnectedToServer()) {
        loadAllData();
      }
    }, 30000);

    // Écouter l'événement connectionLost pour forcer un refresh immédiat
    const handleConnectionLost = () => {
      loadAllData();
    };
    webSocketService.on('connectionLost', handleConnectionLost);

    return () => {
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
      }
      webSocketService.off('connectionLost', handleConnectionLost);
    };
  }, []);

  const value = {
    bains,
    alerts,
    loading,
    error,
    refreshData: loadAllData
  };

  return (
    <SondeContext.Provider value={value}>
      {children}
    </SondeContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useSondeContext = (): SondeContextType => {
  const context = useContext(SondeContext);
  if (context === undefined) {
    throw new Error('useSondeContext doit être utilisé avec un SondeProvider');
  }
  return context;
};
